export interface MarketingSceneProductsCacheState {
  selectedSceneIndex?: number;
  isExpanded?: boolean;
  sceneProductLoaded?: boolean[];
  localSceneProducts?: Array<Array<Record<string, unknown>>>;
  sceneHoverImageRules?: Array<unknown[] | undefined>;
  sceneProductSwiperIndexes?: number[];
}

interface ResolveMarketingSceneProductsCacheStateParams {
  cacheState?: MarketingSceneProductsCacheState;
  sceneCount: number;
  visibleSceneSourceIndexes: number[];
  fallbackSceneIndex: number;
}

export function resolveMarketingSceneProductsCacheState({
  cacheState,
  sceneCount,
  visibleSceneSourceIndexes,
  fallbackSceneIndex,
}: ResolveMarketingSceneProductsCacheStateParams): MarketingSceneProductsCacheState | null {
  if (!cacheState || sceneCount <= 0) {
    return null;
  }

  const sceneProductLoaded =
    Array.isArray(cacheState.sceneProductLoaded) &&
    cacheState.sceneProductLoaded.length === sceneCount
      ? cacheState.sceneProductLoaded
      : undefined;
  const localSceneProducts =
    Array.isArray(cacheState.localSceneProducts) &&
    cacheState.localSceneProducts.length === sceneCount &&
    cacheState.localSceneProducts.every(Array.isArray)
      ? cacheState.localSceneProducts
      : undefined;
  const sceneHoverImageRules =
    Array.isArray(cacheState.sceneHoverImageRules) &&
    cacheState.sceneHoverImageRules.length === sceneCount &&
    cacheState.sceneHoverImageRules.every(
      (imageRule) => imageRule === undefined || Array.isArray(imageRule),
    )
      ? cacheState.sceneHoverImageRules
      : undefined;
  const cachedIndex = Number(cacheState.selectedSceneIndex);
  const cachedIndexHasProducts =
    Number.isFinite(cachedIndex) &&
    cachedIndex >= 0 &&
    cachedIndex < sceneCount &&
    Boolean(localSceneProducts?.[cachedIndex]?.length);
  const selectedSceneIndex =
    Number.isFinite(cachedIndex) &&
    (visibleSceneSourceIndexes.includes(cachedIndex) || cachedIndexHasProducts)
      ? cachedIndex
      : fallbackSceneIndex;
  const sceneProductSwiperIndexes =
    Array.isArray(cacheState.sceneProductSwiperIndexes) &&
    cacheState.sceneProductSwiperIndexes.length === sceneCount &&
    cacheState.sceneProductSwiperIndexes.every((index) => Number.isFinite(Number(index)))
      ? cacheState.sceneProductSwiperIndexes.map((index) => Math.max(0, Math.floor(Number(index))))
      : undefined;

  return {
    selectedSceneIndex,
    isExpanded: Boolean(cacheState.isExpanded),
    sceneProductLoaded,
    localSceneProducts,
    sceneHoverImageRules,
    sceneProductSwiperIndexes,
  };
}
